import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/utils';
import type { WithdrawalAddressItem } from '@shared/api/contracts/security';
import { areAutoWithdrawalAddressesEqual, hasAddressChangedFromPersisted, type AutoWithdrawalRuleDraft } from '@entities/withdrawal';
import { normalizeWithdrawalNetworkCode } from '../../lib/network-balance';
import { formatDateTime } from '@shared/lib/format-date-time';
import styles from './auto-withdraw-ui.module.css';

interface VerificationBadgeProps {
  draft: AutoWithdrawalRuleDraft;
  className?: string;
}

export const VerificationBadge = ({ draft, className }: VerificationBadgeProps) => {
  const { t } = useTranslation('cabinet');

  if (draft.hasVerification === null) {
    return null;
  }

  if (draft.enabled && draft.hasVerification) {
    return (
      <span className="inline-flex flex-col items-start gap-1">
        <span className={cn(styles.badgeOk, className)}>{t('withdrawal.auto.badges.active')}</span>
        <span className="text-xs text-muted-foreground">{t('withdrawal.auto.badges.notImmediate')}</span>
      </span>
    );
  }

  if (draft.enabled && !draft.hasVerification) {
    return (
      <span className={cn(styles.badgeWarn, className)}>
        {t('withdrawal.auto.badges.pendingVerification')}
      </span>
    );
  }

  if (!draft.enabled && draft.hasVerification) {
    return (
      <span className={cn('rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground', className)}>
        {t('withdrawal.auto.badges.disabledVerified')}
      </span>
    );
  }

  return (
    <span className={cn('rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground', className)}>
      {t('withdrawal.auto.badges.disabled')}
    </span>
  );
};

export const AddressChangeNotice = ({ draft }: { draft: AutoWithdrawalRuleDraft }) => {
  const { t } = useTranslation('cabinet');

  if (draft.hasVerification === null) {
    return null;
  }

  if (hasAddressChangedFromPersisted(draft.networkCode, draft.destinationAddress, draft.persistedAddress)) {
    return (
      <p className="text-xs text-muted-foreground">{t('withdrawal.auto.badges.addressWillRequireVerification')}</p>
    );
  }

  if (draft.enabled && !draft.hasVerification) {
    return (
      <p className="text-xs text-muted-foreground">{t('withdrawal.auto.badges.pendingVerificationHint')}</p>
    );
  }

  return null;
};

export const RuleAddressState = ({
  draft,
  items,
  language,
}: {
  draft: AutoWithdrawalRuleDraft;
  items: WithdrawalAddressItem[];
  language: string;
}) => {
  const { t } = useTranslation('cabinet');
  const address = draft.destinationAddress.trim();
  if (!address) return null;

  const network = normalizeWithdrawalNetworkCode(draft.networkCode);
  const match = items.find(
    (item) =>
      normalizeWithdrawalNetworkCode(item.network) === network &&
      areAutoWithdrawalAddressesEqual(draft.networkCode, item.address, address),
  );

  if (!match) {
    return <p className="text-xs text-muted-foreground">{t('withdrawal.auto.addressState.missing')}</p>;
  }

  if (match.status === 'pending') {
    return (
      <p className="text-xs text-muted-foreground">
        {t('withdrawal.auto.addressState.cooling', {
          datetime: formatDateTime(match.activeFrom ?? '', language),
        })}
      </p>
    );
  }

  return <p className="text-xs text-muted-foreground">{t('withdrawal.auto.addressState.active')}</p>;
};
